import { AbstractControl, FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { Component, Input, OnInit, SimpleChanges, OnChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { TranslateModule } from "@ngx-translate/core";
import { PasswordPattern } from "../../../_interfaces/password-pattern";
import { SPECIAL_CHARS_REGEX } from "../../../_helpers/user.helper";

@Component({
    selector: 'app-new-password',
    templateUrl: './new-password.component.html',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, TranslateModule]
})
export class NewPasswordComponent implements OnInit, OnChanges {
    @Input() control!: AbstractControl | null;
    @Input() passwordPattern!: PasswordPattern;
    @Input() label: string = 'New password';
    @Input() placeholder: string = 'Enter new password';
    @Input() confirmPasswordValue: string = '';

    showPassword = false;
    passwordHints: { key: string, label: string, params?: Record<string, unknown> }[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['passwordPattern']) {
            this.generatePasswordHints();
        }
    }

    ngOnInit() {
        this.generatePasswordHints();
    }

    private generatePasswordHints(): void {
        const hints: { key: string, label: string, params?: Record<string, unknown> }[] = [];
        if (!this.passwordPattern) {
            this.passwordHints = hints;
            return;
        }

        if (this.passwordPattern.passwordLength) {
            hints.push({
                key: 'passwordLength',
                label: 'Minimum {{count}} characters',
                params: { count: this.passwordPattern.passwordLength },
            });
        }
        if (this.passwordPattern.passwordLowercase) {
            hints.push({ key: 'passwordLowercase', label: 'Lowercase letter' });
        }
        if (this.passwordPattern.passwordUppercase) {
            hints.push({ key: 'passwordUppercase', label: 'Uppercase letter' });
        }
        if (this.passwordPattern.passwordNumbers) {
            hints.push({ key: 'passwordNumbers', label: 'Number' });
        }
        if (this.passwordPattern.passwordSymbols) {
            hints.push({ key: 'passwordSymbols', label: 'Special character' });
        }

        this.passwordHints = hints;
    }

    hasAnyRules(): boolean {
        return this.passwordHints.length > 0;
    }

    togglePasswordVisibility(): void {
        this.showPassword = !this.showPassword;
    }

    isHintSatisfied(hint: string): boolean {
        const password = this.control?.value || '';
        if (!password) return false;

        switch (hint) {
            case 'passwordLength':
                return password.length >= (this.passwordPattern?.passwordLength || 0);
            case 'passwordLowercase':
                return /[a-z]/.test(password);
            case 'passwordUppercase':
                return /[A-Z]/.test(password);
            case 'passwordUpperLower':
                return /[a-z]/.test(password) && /[A-Z]/.test(password);
            case 'passwordNumbers':
                return /[0-9]/.test(password);
            case 'passwordSymbols':
                return SPECIAL_CHARS_REGEX.test(password);
            case 'noWhitespace':
                return !/\s/.test(password);
            case 'passwordsMatch':
                return !!this.confirmPasswordValue && password === this.confirmPasswordValue;
            default:
                return false;
        }
    }

    getControl(): FormControl | null {
        return this.control as FormControl;
    }

    hasLengthValidator(): boolean {
        return this.passwordPattern?.passwordLength !== undefined && this.passwordPattern?.passwordLength > 0;
    }
}